import { useEffect, useRef } from "react";
import { sendMessage } from "utils/webview";
import { isAdTrackingSuppressed } from "analytics/adTracking";
import { sendGoogleAdsAppEvent } from "analytics/googleAdsAppEvent";

type Params = {
  user: any;
  isInappWebview?: boolean;
  appVersion: string;
  setMixPanel: () => void;
};

/**
 * 결제 페이지 진입 시 공통 트래킹 훅
 * 순서: FB → Firebase/Appsflyer → Google Ads(서버) → Mixpanel
 * 가드: suppress_payment_page_view(localStorage) + sentEventRef(중복 방지)
 */
export default function usePaymentPageViewTracking({
  user,
  isInappWebview,
  setMixPanel,
}: Params) {
  const sentEventRef = useRef(false);

  // 결제 성공 후 재진입 시 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송 차단 플래그
  const initialSuppress = (() => {
    try {
      return localStorage.getItem("suppress_payment_page_view") === "1";
    } catch (e) {
      return false;
    }
  })();
  const suppressPageViewRef = useRef<boolean>(initialSuppress);

  // suppress 플래그는 1회성으로 사용 후 제거
  useEffect(() => {
    if (suppressPageViewRef.current) {
      try {
        localStorage.removeItem("suppress_payment_page_view");
      } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (!sentEventRef.current && !suppressPageViewRef.current) {
      // FB event (인사이더/제휴 프로모션 사용자는 광고 추적 제외, Mixpanel 만 전송)
      if (!isAdTrackingSuppressed(user) && isInappWebview) {
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "page_view_payment",
            },
          })
        );

        sendMessage(
          JSON.stringify({
            event: "send_appsflyer_event",
            params: {
              eventName: "af_page_view_payment",
              eventParams: {
                af_content_id: "distancing_program",
              },
            },
          })
        );

        sendMessage(
          JSON.stringify({
            event: "send_firebase_analytics_event",
            params: {
              eventName: "screen_view",
              eventParams: {
                screen_name: "PaymentScreen",
                screen_class: "PaymentScreenComponent",
                // GA4 잠재고객·맞춤 이벤트용 세그먼트 파라미터 (구 screen_view_payment_type_a 별도 이벤트 대체)
                ...(user?.segmentType
                  ? { segment_type: user.segmentType }
                  : {}),
              },
            },
          })
        );
      }

      // Google Ads "결제 페이지 뷰 (API)" — 서버가 웹퍼널(Google) 유입 유저만 올린다. 브리지가 필요 없어 브라우저에서도 보낸다
      if (!isAdTrackingSuppressed(user)) {
        sendGoogleAdsAppEvent("payment_page_view");
      }

      // Mixpanel
      setMixPanel();

      sentEventRef.current = true;
    }
  }, [user?.isInsider, user?.promotion, user?.segmentType, isInappWebview]);

  return { sentEventRef, suppressPageViewRef } as const;
}
